import { Fragment } from 'react'
import { bewegungsarteintrag, kurzform, t, vortragsart, vortragsname } from './sprache'
import type { Sprachschluessel } from './sprache'
import { eigeneZellen, moeglicheAchsen, vorgabeFuer } from './Aufriss'
import type { Zelle } from './Aufriss'
import { Aufrisszeilen, ebenenschluessel, menueort, zellkontext, zusatzachsen } from './Aufrisszeilen'
import type { Achsenquelle, Menueruf } from './Aufrisszeilen'
import {
  berichtsmenue,
  einzug,
  istAufreissbar,
  zeilenachsen,
  istTrefferzelle,
  trefferklasse,
  Wert,
  Zeilenbeschriftung,
  Zeilenkopf,
  rangklasse,
  zeilenname,
  Zellen,
} from './Bericht'
import type { Aufrissbedienung, Nachweisruf, Trefferzustand } from './Bericht'
import { alleNull, ohneLeereZeilen, unterdrueckt, useAnzeigeoptionen } from './Anzeigeoptionen'
import { useGliederungsbild } from './Gliederung'
import { Spaltenleiste, useSpaltenbreiten } from './Spaltenbreiten'
import { zeilenId } from './Suche'
import { Zeilenmenue } from './Zeilenmenue'
import type { Eigenschaftsziel } from './Eigenschaftsfenster'
import type { Spiegel as Spiegelsatz, Spiegelaufriss, Spiegelspalte } from './typen'

/**
 * Anlagen-, Eigenkapital- und Kreditspiegel — ein Bauteil für drei.
 *
 * **Drei Reihen nebeneinander:** Anschaffungs- und Herstellungskosten,
 * kumulierte Abschreibungen, Buchwerte. Ein Abgang steht in den ersten beiden
 * mit zwei verschiedenen Beträgen; erst ihre Differenz ist der Restbuchwert,
 * und genau der wird **nicht gebucht**, sondern gerechnet (2.5).
 *
 * Er liest dieselben festgeschriebenen Ergebniszeilen wie die Bilanz — nur die
 * mit Bewegungsart. Deshalb kann sein Endbestand von der Bilanz gar nicht
 * abweichen; die Tabelle muss es nicht dazusagen.
 *
 * **Und er wird bedient wie ein Bericht:** dasselbe Menü, derselbe Aufriss
 * als Pfad, derselbe Doppelklick. Der Nachweis einer Bewegungsspalte zeigt
 * die Bewegungen dieser Spalte je Monat und prüft sich gegen die Spiegelzelle
 * (R186, R208). Die gerechneten Spalten — Endbestand eines Blocks, Buchwerte
 * — tragen keinen: Sie sind Summen, keine Herkunft.
 */
/**
 * Der Spiegel ohne seine leeren Bewegungsspalten (R707, 2.5 Abschnitt 9).
 *
 * **Die gerechneten Spalten bleiben stehen** — Endbestand und Buchwerte. Der
 * Haken heißt *Bewegungsarten*, und ein Endbestand ohne Zahl ist eine Aussage
 * und keine leere Spalte.
 *
 * **Gestrichen wird an jeder Stelle zugleich**: Spaltenköpfe, Zeilenwerte und
 * die Werte jeder Aufrisszelle stehen in derselben Reihenfolge, und eine Zahl,
 * die eine Stelle verrutscht, stünde unter dem falschen Kopf.
 *
 * **Gezählt wird auch im Aufriss** und nicht nur in der Zeile: Zwei
 * Gesellschaften können sich zu null aufheben. Die Spalte ist dann in der
 * Summe leer und trägt trotzdem zwei Beträge.
 */
function ohneLeereSpalten(spiegel: Spiegelsatz): Spiegelsatz {
  const gerechnet = (s: Spiegelspalte) => s.block === 'buchwert' || s.spalte === 'endbestand'

  const belegt = spiegel.spalten.map(
    (s, i) =>
      gerechnet(s) ||
      spiegel.zeilen.some(
        (z) => z.werte[i] !== 0 || z.beitraege.some((b) => b.werte[i] !== 0),
      ),
  )

  if (belegt.every(Boolean)) {
    return spiegel
  }

  const behalten = <T,>(werte: T[]): T[] => werte.filter((_, i) => belegt[i])

  return {
    ...spiegel,
    spalten: behalten(spiegel.spalten),
    zeilen: spiegel.zeilen.map((z) => ({
      ...z,
      werte: behalten(z.werte),
      beitraege: z.beitraege.map((b) => ({ ...b, werte: behalten(b.werte) })),
    })),
  }
}

export function Spiegel({
  spiegel: ganz,
  aufriss,
  quelle,
  treffer,
  aufNachweis,
  aufEigenschaften,
}: {
  spiegel: Spiegelsatz
  aufriss: Aufrissbedienung
  quelle?: Achsenquelle<Spiegelaufriss>
  treffer?: Trefferzustand
  /** Doppelklick auf eine Zahl öffnet ihren Wertenachweis (R204). */
  aufNachweis?: Nachweisruf
  /** Rechtsklick → Eigenschaften (R858). */
  aufEigenschaften?: (ziel: Eigenschaftsziel) => void
}) {
  const { menue, setMenue } = aufriss

  // **Auch der Spiegel zeigt die ganze Gliederung** (7.3 Abschnitt 11) -- eine
  // Zeile aus Nullen sagt hier etwas: kein Zugang, kein Abgang.
  const optionen = useAnzeigeoptionen()

  // **Und das gilt seit dem 2026-09-22 auch für die Spalten** (R707).
  //
  // Bis dahin ließ der Dienst jede Spalte weg, in der nichts stand — die
  // Unterdrückung war die Bauweise und nirgends abzuschalten, und eine neu
  // angelegte Bewegungsart erschien im Spiegel erst, nachdem jemand auf ihr
  // gebucht hatte. Jetzt liefert er seinen ganzen Katalog (R706), und der
  // Haken über dem Bericht entscheidet.
  const spiegel = unterdrueckt(optionen, 'bewegungsarten') ? ohneLeereSpalten(ganz) : ganz

  // Je Spiegel eine Breite (7.3, Abschnitt 10) — der Anlagenspiegel hat andere Spalten
  // als der Kreditspiegel. Der Haken steht VOR dem leeren Spiegel: Ein Haken,
  // den manche Durchgänge auslassen, ist keiner.
  const { breiten, rahmen } = useSpaltenbreiten(`spiegel:${spiegel.art}`)
  const vollstaendig = unterdrueckt(optionen, 'positionen')
    ? ohneLeereZeilen(spiegel.zeilen, (z) => alleNull(z.werte))
    : spiegel.zeilen

  // **Und der Spiegel klappt zu wie Bilanz und GuV** (7.3 Abschnitt 13,
  // R447): Seine Gliederung kommt aus demselben Positionsplan.
  const gliederung = useGliederungsbild(vollstaendig)
  const zeilen = gliederung.zeilen

  if (spiegel.zeilen.length === 0) {
    return <p className="hinweis">{t('spiegel.leer')}</p>
  }

  const aufMenue: Menueruf = (x, y, position, eltern, zelle, kontext) =>
    setMenue({ x, y, position, eltern, zelle, kontext })

  // Die Kopfzeile hat zwei Stockwerke: oben der Block, darunter die Spalte.
  // Ein Anlagenspiegel ohne Blocküberschriften ist eine Zahlenwand.
  const bloecke: { block: string; anzahl: number }[] = []
  for (const s of spiegel.spalten) {
    const letzter = bloecke.at(-1)
    if (letzter && letzter.block === s.block) {
      letzter.anzahl += 1
    } else {
      bloecke.push({ block: s.block, anzahl: 1 })
    }
  }

  // Die Spalte heißt nach ihrer Bewegungsart, nicht nach ihrem Block: Ein
  // Anfangsbestand heißt in jedem Block „Anfangsbestand".
  //
  // **Und die Bewegungsart benennt sich selbst** (R239). Bis zum 2026-09-09
  // stand hier ein Sprachschlüssel, und solange nur die zehn ab Werk
  // gelieferten Kernbewegungsarten vorkamen, fiel das nicht auf: Eine
  // kundeneigene Bewegungsart wie „Zugang aus Leasing" (R31) hatte schlicht
  // keine Überschrift, und niemand konnte eine nachtragen, ohne die
  // Oberfläche neu zu übersetzen.
  //
  // Der Rückfall auf die Sprachdatei bleibt und ist kein Rest: `endbestand`
  // und der Buchwertblock sind **gerechnet** und keine Bewegungsart — sie
  // tragen deshalb keine Beschriftung aus dem Stammdatum und sollen auch
  // keine bekommen.
  /*
    **Die Nummer gehört in die Pflege, nicht in den Spaltenkopf** (R830,
    2026-09-27). Seit die Bewegungsart ihre Nummer im Namen trägt — „010
    Zugang" —, stünde sie über jeder Spalte des Spiegels. Eine Überschrift
    beantwortet die Frage *was steht hier*, und dafür trägt die Nummer
    nichts bei; die Spalte daneben trüge zudem ihr Kürzel ohne Nummer, und
    damit stünde die Hälfte der Köpfe nummeriert und die andere nicht.
  */
  const ohneNummer = (text: string) => text.replace(/^\d{1,4}\s+/, '')

  /*
    **Der Kopf trägt das Kürzel, das heute gilt** (R1014, 2026-10-04) — nicht
    das aus dem Stand des Laufs. Ein Lauf friert die Zahlen ein; wer eine
    Bewegungsart umbenennt, soll es im Spiegel sehen, ohne neu zu rechnen
    (R706). Fehlt die Art im Katalog, gilt, was der Lauf mitbringt.
  */
  const spaltenname = (s: Spiegelspalte) => {
    // **Ein Vortrag heißt fest** (R1062): Anfangsbestand, Kumulierte
    // Abschreibung Anfang, Ergebnis der Vormonate — aus der Sprachdatei. Er
    // ist keine Bewegungsart, also trägt er auch nicht ihren Namen.
    const vortrag = vortragsart(s.spalte)
    if (vortrag) return vortragsname(vortrag)
    const geltend = bewegungsarteintrag(s.spalte)
    const kuerzel = geltend ? geltend.kuerzel : s.kuerzel
    const namen = geltend ? geltend.namen : s.namen
    return namen || kuerzel
      ? ohneNummer(kurzform(kuerzel, namen, s.spalte))
      : t(`spiegel.spalte.${s.spalte}` as Sprachschluessel)
  }
  const istEnde = (s: Spiegelspalte) => s.spalte === 'endbestand'

  // **Der Name des Berichts steht in der Tabelle** (R411, seit R475 auch hier).
  // Er stand bis zum 2026-09-13 als Überschrift darüber, und links oben im Kopf
  // war ein leeres Feld: Die Blockzeile hat über der Positionsspalte nichts zu
  // sagen. Jetzt steht er dort — eine Zeile Bildschirm gespart, und „Position"
  // fällt mit ihm weg (das Wort erklärt eine Spalte, die sich selbst erklärt).
  //
  // **Abgeleitet und nicht durchgereicht:** Der Spiegel weiß, welcher Art er
  // ist. Ein durchgereichter Titel wäre eine zweite Wahrheit neben der
  // Menüleiste.
  const berichtsname = t(
    spiegel.art === 'eigenkapital'
      ? 'bereich.berichte.eigenkapitalspiegel'
      : spiegel.art === 'verbindlichkeiten'
        ? 'bereich.berichte.kreditspiegel'
        : 'bereich.berichte.spiegel',
  )

  /**
   * Welchen Nachweis eine Spalte trägt: eine Bewegungsspalte den ihrer
   * Bewegungsarten; der Buchwert am Ende den der Bilanzposition, weil er
   * dieselbe Zahl ist (R186); die übrigen gerechneten Spalten keinen.
   */
  function nachweisVon(s: Spiegelspalte): { spalte: string; spiegel?: { block: string; spalte: string } } | null {
    if (s.block === 'buchwert') {
      return s.spalte === 'endbestand' ? { spalte: 'konzern' } : null
    }

    return istEnde(s) ? null : { spalte: 'konzern', spiegel: { block: s.block, spalte: s.spalte } }
  }

  /** Die Wertzellen einer Zeile oder Aufrisszelle — eine je Spiegelspalte. */
  const werte = (position: string, name: string, z: Zelle, zahlen: number[], kennung: string) => (
    <Zellen ruf={aufNachweis ?? null} position={position} positionsname={name} zelle={z}>
      {zahlen.map((w, i) => {
        const s = spiegel.spalten[i]!
        const nachweis = nachweisVon(s)
        return (
          <Wert
            key={i}
            wert={w}
            klasse={istEnde(s) ? 'spaltensumme' : undefined}
            spalte={nachweis?.spalte}
            spiegel={nachweis?.spiegel}
            fund={istTrefferzelle(treffer, kennung, String(i))}
          />
        )
      })}
    </Zellen>
  )

  return (
    <div className="tabellenrahmen" ref={rahmen}>
      <table className="spiegel positionstabelle">
        <Spaltenleiste breiten={breiten} spalten={1 + spiegel.spalten.length} />
        <thead>
          {bloecke.length > 1 && (
            <tr>
              <th className="berichtstitel">{berichtsname}</th>
              {bloecke.map((b) => (
                <th key={b.block} colSpan={b.anzahl} className="spiegelblock">
                  {t(`spiegel.block.${b.block}` as Sprachschluessel)}
                </th>
              ))}
            </tr>
          )}
          <tr>
            {/*
              **Wo es keine Blockzeile gibt, steht der Name hier** — im
              Eigenkapital- und im Kreditspiegel, die nur einen Block tragen.
              Dieselbe Stelle wie in Bilanz und GuV (R411). Eine Regel, zwei
              Erscheinungsformen: Die Tabelle entscheidet, nicht der Bericht.
            */}
            <th className={`positionsspalte ${bloecke.length > 1 ? '' : 'berichtstitel'}`.trim()}>
              {bloecke.length > 1 ? '' : berichtsname}
            </th>
            {spiegel.spalten.map((s) => (
              <th
                key={`${s.block}-${s.spalte}`}
                className={`zahl ${istEnde(s) ? 'spaltensumme' : ''}`.trim()}
              >
                {spaltenname(s)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {zeilen.map((zeile) => {
            const achse = aufriss.zustand.get(ebenenschluessel(zeile.position, []))
            const vorgabe = vorgabeFuer(zeile.beitraege)
            const moeglich = zeilenachsen(zeile, moeglicheAchsen(zeile.beitraege, [], zusatzachsen(quelle)))
            const aufreissbar = istAufreissbar(zeile, moeglich)
            const id = zeilenId(zeile.position, [])
            const leer: Zelle = { einheit: null, konto: null }
            const name = zeilenname(zeile)

            return (
              <Fragment key={zeile.position}>
                <tr
                  id={id}
                  className={[
                    rangklasse(zeile),
                    aufreissbar ? 'aufklappbar' : '',
                    // Keine Dämpfung für eine Zeile ohne Betrag (R867, 7.3 Abschnitt 21):
                    // Das leere Wertfeld sagt es, und jede Zeile steht in der Farbe ihrer Ebene.
                    trefferklasse(treffer, id),
                  ]
                    .join(' ')
                    .trim()}
                  tabIndex={0}
                  onContextMenu={(e) => {
                    e.preventDefault()
                    const ort = menueort(e, e.currentTarget)
                    aufMenue(ort.x, ort.y, zeile.position, [], leer, zellkontext(e))
                  }}
                >
                  <td style={{ paddingLeft: einzug(zeile.ebene) }}>
                    <Zeilenkopf
                      aufreissbar={aufreissbar}
                      achse={achse}
                      vorgabe={vorgabe}
                      angebot={quelle}
                      aufKlappen={() =>
                        aufriss.umschalten(zeile.position, [], achse ?? moeglich[0] ?? vorgabe)
                      }
                      aufMenue={(x, y) => aufMenue(x, y, zeile.position, [], leer, {})}
                      klappbar={gliederung.klappbar(zeile.position)}
                      zugeklappt={gliederung.zugeklappt(zeile.position)}
                      aufGliederung={() => gliederung.umschalten(zeile.position)}
                    >
                      <Zeilenbeschriftung zeile={zeile} />
                    </Zeilenkopf>
                  </td>
                  {werte(zeile.position, name, leer, zeile.werte, id)}
                </tr>

                {/*
                  **Nur unter einer Zeile, die aufgerissen werden darf** (R165, R432).
                  Pfeil und Menü führen nicht auf eine Summenzeile — aber der
                  Aufklappzustand kommt auch von woanders: aus einem anderen Bericht,
                  aus der Adresse, und seit heute aus einem Suchtreffer. Ohne diesen
                  Riegel zeichnet die Tabelle, was kein Weg ihr hätte sagen dürfen.
                */}
                {aufreissbar && (
                  <Aufrisszeilen
                    position={zeile.position}
                    summe={zeile.istSumme}
                    ebeneTiefe={zeile.ebene}
                    zellen={zeile.beitraege}
                    eltern={[]}
                    aufriss={aufriss}
                    quelle={quelle}
                    vorgabe={vorgabe}
                    spaltenzahl={spiegel.spalten.length + 1}
                    treffer={treffer}
                    aufMenue={aufMenue}
                    werteSpalten={(z, kennung) => werte(zeile.position, name, z, z.werte, kennung)}
                  />
                )}

                {/* Die eigenen Konten vor den Unterpositionen (7.3 Abschnitt 30, R1621). */}
                {!gliederung.zugeklappt(zeile.position) && eigeneZellen(zeile).length > 0 && (
                  <Aufrisszeilen
                    position={zeile.position}
                    fest="konten"
                    ebeneTiefe={zeile.ebene}
                    zellen={eigeneZellen(zeile)}
                    eltern={[]}
                    aufriss={aufriss}
                    quelle={quelle}
                    vorgabe={vorgabe}
                    spaltenzahl={spiegel.spalten.length + 1}
                    treffer={treffer}
                    aufMenue={aufMenue}
                    werteSpalten={(z, kennung) => werte(zeile.position, name, z, z.werte, kennung)}
                  />
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>

      {menue && (
        <Zeilenmenue
          x={menue.x}
          y={menue.y}
          punkte={berichtsmenue({
            menue,
            aufEigenschaften,
            bericht: spiegel,
            aufriss,
            quelle,
            aufNachweis,
            gliederung: gliederung.menue,
          })}
          aufSchliessen={() => setMenue(null)}
        />
      )}
    </div>
  )
}
